import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsersCog } from "@fortawesome/free-solid-svg-icons";

import { DataContext } from "../../../context/DataContext";
import { getToken } from "../../../api/Account";
import { getRuta } from "../../../helpers/Helpers";
import { tienePermiso } from "../../../helpers/permisos";
import {
  deleteUserAsync,
  getAllUserAsync,
  getInactiveUserListAsync,
  getUserListAsync,
  reactivateUserAsync,
  resetUserPasswordAsync,
} from "../../../api/Users";
import {
  CargandoTabla,
  EstadoChip,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../../components/base";
import {
  PAGINACION_ARIA,
  encabezadoTabla,
  normalizar,
  useCargaLocal,
  useConfirmacion,
  useEscritura,
} from "../comunes";

const FILAS_POR_PAGINA = 10;
const CLAVE_POR_DEFECTO = "123456";

// Las tres listas de siempre: cada una con su endpoint.
const FILTROS = [
  { etiqueta: "Activos", pedir: getUserListAsync, cargando: "Cargando usuarios activos…" },
  { etiqueta: "Inactivos", pedir: getInactiveUserListAsync, cargando: "Cargando usuarios inactivos…" },
  { etiqueta: "Todos", pedir: getAllUserAsync, cargando: "Cargando usuarios…" },
];
const ACTIVOS = 0;
const INACTIVOS = 1;
const TODOS = 2;

const Userlist = () => {
  const [userList, setUserList] = useState([]);
  const [localReload, setLocalReload] = useState(false);
  const [filtro, setFiltro] = useState(ACTIVOS);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagina, setPagina] = useState(0);
  // Menú de tres puntos de una fila.
  const [menu, setMenu] = useState({ ancla: null, fila: null });

  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir: pedirConfirmacion, dialogo: confirmacion } = useConfirmacion();

  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();

  const { cargando, error, cargar } = useCargaLocal(() => FILTROS[filtro].pedir(token));

  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setUserList(result.data.result);
    })();
    return () => {
      vigente = false;
    };
  }, [localReload, filtro]);

  const termino = normalizar(searchTerm.trim());
  const withSearch = userList.filter((val) => {
    if (termino === "") return true;
    return (
      normalizar(val.id).includes(termino) ||
      normalizar(val.fullName).includes(termino) ||
      normalizar(val.userName).includes(termino) ||
      normalizar(val.userRol?.rolName).includes(termino)
    );
  });

  // Si al buscar, cambiar de filtro o desactivar la página actual queda fuera de rango, se vuelve
  // a la primera: así no aparece un "No hay datos" falso.
  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= withSearch.length) setPagina(0);
  }, [pagina, withSearch.length]);

  const currentItem = withSearch.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  const onChangeSearch = (val) => {
    setPagina(0);
    setSearchTerm(val);
  };

  const onChangeFiltro = (valor) => {
    if (valor === filtro) return;
    setPagina(0);
    setFiltro(valor);
  };

  const recargar = () => setLocalReload((actual) => !actual);

  const desactivar = async (fila) => {
    const ok = await escribir(
      () => deleteUserAsync(token, fila.id),
      "No se pudo desactivar al usuario"
    );
    if (!ok) return;
    recargar();
    aviso.exito(`Usuario ${fila.userName} desactivado`);
  };

  const reactivar = async (fila) => {
    const ok = await escribir(
      () => reactivateUserAsync(token, fila.id),
      "No se pudo reactivar al usuario"
    );
    if (!ok) return;
    recargar();
    aviso.exito(`Usuario ${fila.userName} reactivado`);
  };

  const restablecerClave = async (fila) => {
    const ok = await escribir(
      () => resetUserPasswordAsync(token, fila.id),
      "No se pudo restablecer la contraseña"
    );
    if (!ok) return;
    aviso.exito(`Contraseña de ${fila.userName} restablecida: ahora es ${CLAVE_POR_DEFECTO}`);
  };

  const pedirDesactivar = (fila) =>
    pedirConfirmacion({
      titulo: "Desactivar usuario",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`${fila.fullName} (${fila.userName}) ya no podrá entrar al sistema ni a la app.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            No se borra nada: se puede reactivar desde la pestaña Inactivos.
          </Typography>
        </>
      ),
      textoConfirmar: "Desactivar usuario",
      peligro: true,
      onConfirmar: () => desactivar(fila),
    });

  const pedirReactivar = (fila) =>
    pedirConfirmacion({
      titulo: "Reactivar usuario",
      mensaje: `${fila.fullName} (${fila.userName}) podrá volver a entrar al sistema y a la app, con su rol de ${fila.userRol?.rolName ?? "siempre"}.`,
      textoConfirmar: "Reactivar usuario",
      onConfirmar: () => reactivar(fila),
    });

  const pedirRestablecer = (fila) =>
    pedirConfirmacion({
      titulo: "Restablecer contraseña",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`La contraseña de ${fila.fullName} (${fila.userName}) pasará a ser ${CLAVE_POR_DEFECTO}. La actual deja de servir.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Pídele que la cambie en Mi cuenta en cuanto entre.
          </Typography>
        </>
      ),
      textoConfirmar: "Restablecer contraseña",
      peligro: true,
      onConfirmar: () => restablecerClave(fila),
    });

  const puedeAgregar = tienePermiso(access, "SECURITY CREATE");
  const puedeEditar = tienePermiso(access, "SECURITY UPDATE");
  const puedeActivar = tienePermiso(access, "SECURITY DELETE");

  const accionesDeFila = (fila) => [
    puedeEditar && fila.isActive
      ? { texto: "Restablecer contraseña", Icono: KeyOutlinedIcon, peligro: false, alElegir: pedirRestablecer }
      : null,
    puedeActivar && fila.isActive
      ? { texto: "Desactivar", Icono: PersonOffOutlinedIcon, peligro: true, alElegir: pedirDesactivar }
      : null,
    puedeActivar && !fila.isActive
      ? { texto: "Reactivar", Icono: HowToRegOutlinedIcon, peligro: false, alElegir: pedirReactivar }
      : null,
  ].filter(Boolean);

  const hayBusqueda = termino !== "";

  const botonAgregar = (
    <Button
      onClick={() => navigate(`${ruta}/adduser`)}
      startIcon={<AddIcon />}
      variant="contained"
    >
      Agregar usuario
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={6} columnas={5} etiqueta={FILTROS[filtro].cargando} />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los usuarios"
        }
        onReintentar={recargar}
      />
    );
  } else if (withSearch.length === 0) {
    if (hayBusqueda) {
      contenido = (
        <EstadoVacio
          titulo="Sin resultados"
          descripcion={`Ningún usuario coincide con «${searchTerm.trim()}». Revisa el nombre, el usuario o el rol.`}
          accion={
            <Button variant="outlined" onClick={() => onChangeSearch("")}>
              Borrar búsqueda
            </Button>
          }
        />
      );
    } else if (filtro === INACTIVOS) {
      contenido = (
        <EstadoVacio
          titulo="No hay usuarios inactivos"
          descripcion="Aquí aparecen los usuarios desactivados. Desde aquí se pueden reactivar."
        />
      );
    } else {
      contenido = (
        <EstadoVacio
          titulo="Todavía no hay usuarios"
          descripcion="Aquí aparecen las personas que entran al sistema y a la app, con su rol."
          accion={puedeAgregar ? botonAgregar : undefined}
        />
      );
    }
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label={`Usuarios ${FILTROS[filtro].etiqueta.toLowerCase()}`}>
            <TableHead>
              <TableRow>
                <TableCell sx={encabezadoTabla}>Usuario</TableCell>
                <TableCell sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}>
                  Nombre completo
                </TableCell>
                <TableCell sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}>
                  Teléfono
                </TableCell>
                <TableCell sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}>
                  Rol
                </TableCell>
                {filtro === TODOS ? (
                  <TableCell sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}>
                    Estado
                  </TableCell>
                ) : null}
                <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => {
                const acciones = accionesDeFila(row);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {row.userName}
                      </Typography>
                      {/* En el teléfono el nombre y el rol pasan debajo del usuario. */}
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: { xs: "block", sm: "none" } }}
                      >
                        {row.fullName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: { xs: "block", sm: "none" } }}
                      >
                        {row.userRol?.rolName ?? "—"}
                        {filtro === TODOS ? ` · ${row.isActive ? "Activo" : "Inactivo"}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      {row.fullName}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                        display: { xs: "none", md: "table-cell" },
                      }}
                    >
                      {row.phone || "—"}
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      {row.userRol?.rolName ?? "—"}
                    </TableCell>
                    {filtro === TODOS ? (
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                        <EstadoChip
                          estado={row.isActive ? "ok" : "neutro"}
                          label={row.isActive ? "Activo" : "Inactivo"}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Tooltip title={puedeEditar ? "Editar usuario" : "Ver usuario"}>
                        <IconButton
                          aria-label={`${puedeEditar ? "Editar" : "Ver"} al usuario ${row.userName}`}
                          onClick={() => navigate(`${ruta}/user/${row.id}`)}
                          sx={{ color: "primary.main" }}
                        >
                          {puedeEditar ? <EditOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                      {acciones.length > 0 ? (
                        <Tooltip title="Más acciones">
                          <IconButton
                            aria-label={`Más acciones de ${row.userName}`}
                            aria-haspopup="menu"
                            onClick={(e) => setMenu({ ancla: e.currentTarget, fila: row })}
                            sx={{ color: "text.secondary" }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={withSearch.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={PAGINACION_ARIA}
        />
      </>
    );
  }

  const accionesMenu = menu.fila ? accionesDeFila(menu.fila) : [];

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Usuarios"
        subtitulo="Quién entra al sistema y a la app, con qué rol"
        icono={<FontAwesomeIcon icon={faUsersCog} />}
        acciones={puedeAgregar ? botonAgregar : undefined}
      />

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Tabs
          value={filtro}
          onChange={(_e, indice) => onChangeFiltro(indice)}
          aria-label="Estado del usuario"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          {FILTROS.map((f, indice) => (
            <Tab
              key={f.etiqueta}
              label={f.etiqueta}
              id={`usuarios-tab-${indice}`}
              aria-controls="usuarios-lista"
            />
          ))}
        </Tabs>

        <TextField
          size="small"
          type="search"
          onChange={(e) => onChangeSearch(e.target.value)}
          value={searchTerm}
          placeholder="Nombre, usuario o rol"
          inputProps={{ "aria-label": "Buscar usuario", autoComplete: "off" }}
          sx={{ width: { xs: "100%", md: 380 }, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Borrar búsqueda"
                  onClick={() => onChangeSearch("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <div role="tabpanel" id="usuarios-lista" aria-labelledby={`usuarios-tab-${filtro}`}>
          {contenido}
        </div>
      </Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        {accionesMenu.map(({ texto, Icono, peligro, alElegir }) => (
          <MenuItem
            key={texto}
            onClick={() => {
              const fila = menu.fila;
              setMenu((actual) => ({ ...actual, ancla: null }));
              alElegir(fila);
            }}
            sx={peligro ? { color: "error.main" } : undefined}
          >
            <ListItemIcon sx={peligro ? { color: "error.main" } : undefined}>
              <Icono />
            </ListItemIcon>
            <ListItemText>{texto}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {confirmacion}
    </Container>
  );
};

export default Userlist;
